import React, { useState } from 'react';
import Box from '@material-ui/core/Box';
import { connect } from 'react-redux';
import { Field, Form, Formik } from 'formik';
import { bindActionCreators, compose } from 'redux';
import FormTextField from '../../../common/form/FormTextField';
import { Paper, Step, StepLabel, Stepper } from '@material-ui/core';
import { fieldCustomStyles } from '../AddNewItemDialog';
import {
  BACKGROUND_DARK, BORDER_DARK_TEXT, DARK_CUSTOM, DARK_UPDATED_FIELD, EXPAND_ROW_COLOR_DARK,
  GREEN_COLOR, GREY_COLOR, LIGHT_GREY_COLOR, LIGHT_UPDATED_FIELD, VERY_LIGHT_GREY_COLOR, WHITE_COLOR,
} from '../../../../const/color-const';
import { CONFIG_BOOKKEEPING, CONFIG_BOOKKEEPING_NEXT_STEP } from './AdministrationBookkeepingConfig';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { AddOutlined, ArrowBack, ArrowForward, MergeTypeSharp } from '@material-ui/icons';
import CommonButton from '../../../common/CommonButton';
import {
  fetchAdministrationData,
  fetchVATOptions,
  saveBookkeepingData,
} from '../../../../actions/administration/administration-actions';
import { LOCAL_STORAGE_SESSION_ID } from '../../../../actions/auth-actions';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import Lottie from 'lottie-react';
import successAnimation from '../../../../style/animations/success.json';
import errorAnimation from '../../../../style/animations/error.json';
import PropTypes from 'prop-types';

const useStyles = makeStyles(theme => ({
  paperWrapper: {
    background: theme.palette.type === 'light' ? WHITE_COLOR : DARK_CUSTOM,
    marginBottom: theme.spacing(16),
    width: 'auto',
    [theme.breakpoints.up('md')]: {
      margin: theme.spacing(0, 2, 16, 2),
    },
    [theme.breakpoints.up('lg')]: {
      width: '50%',
    },
  },
  stepContainer: {
    padding: theme.spacing(5, 0, 3, 0),
    background: theme.palette.type === 'light' ? WHITE_COLOR : DARK_CUSTOM,
    borderBottom: theme.palette.type === 'light' ? `1px solid ${VERY_LIGHT_GREY_COLOR}` : `1px solid ${DARK_UPDATED_FIELD}`,
  },
  formWrapper: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    '& > div': {
      '& [class*="isUpdated"]': {
        backgroundColor: (theme.palette.type === 'light' ? LIGHT_UPDATED_FIELD : DARK_UPDATED_FIELD) + ' !important',
      },
      '& [class*="MuiTextField"]': {
        ...fieldCustomStyles(theme),
        border: 'none',
        height: 52,
        '& > div': {
          height: 52,
          '& fieldset': {
            border: theme.palette.type === 'light' ? 'none' : `1px solid ${BORDER_DARK_TEXT}`,
          },
        },
        '& .Mui-error': {
          marginTop: 0,
        },
      },
    },
  },
  formHeader: {
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  buttonsWrapper: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(3, 5, 1, 5),
    marginTop: theme.spacing(4),
    [theme.breakpoints.up('xl')]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
  },
  buttonsStepWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    marginTop: theme.spacing(4),
    justifyContent: 'space-between',
  },
  stepWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 250,
    '& > div': {
      width: '100%',
      flexWrap: 'wrap',
    },
    '& > p': {
      fontWeight: 'bold',
      fontSize: 16,
    },
    '& button': {
      minWidth: 220,
      alignSelf: 'center',
      color: GREEN_COLOR,
      borderColor: GREEN_COLOR,
      background: theme.palette.type === 'light' ? 'white' : BACKGROUND_DARK,
    },
  },
  successHeader: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(0, 2, 0, 2),
    marginTop: 0,
    marginBottom: 0,
    height: 220,
  },
  bookkeepingTable: {
    display: 'flex',
    flexDirection: 'column',
    border: theme.palette.type === 'light' ? `1px solid ${LIGHT_GREY_COLOR}` : `1px solid ${GREY_COLOR}`,
    borderRadius: '5px',
    overflow: 'hidden',
    width: '100%',
    maxWidth: '400px',
  },
  headerRow: {
    height: 36,
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: theme.palette.type === 'light' ? VERY_LIGHT_GREY_COLOR : EXPAND_ROW_COLOR_DARK,
    borderBottom: theme.palette.type === 'light' ? `1px solid ${LIGHT_GREY_COLOR}` : `1px solid ${GREY_COLOR}`,
  },
  dataRow: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    borderBottom: theme.palette.type === 'light' ? `1px solid ${LIGHT_GREY_COLOR}` : `1px solid ${GREY_COLOR}`,
  },
  cell: {
    flex: 1,
    width: '100%',
    padding: '8px',
    fontWeight: 'bold',
    textAlign: 'center',
  },
}));

const SESSION_ID = localStorage.getItem(LOCAL_STORAGE_SESSION_ID);
const DINKASSA_V1 = 'https://www.dinkassa.se/v1/Default.aspx?';
const INTEGRATION_URL = `${DINKASSA_V1}seid=${SESSION_ID}&path=Machines.aspx`;

const commonStyles = { marginTop: 0, marginBottom: 0 };
const successStyles = { ...commonStyles, marginTop: 30, width: 60, height: 60 };
const errorStyles = { ...commonStyles, marginTop: 40, width: 80, height: 80 };

const AdministrationBookkeepingContent = ({
                                            machineId,
                                            setPopupActive,
                                            fetchAdministrationData,
                                            saveBookkeepingData,
                                            administrationBookkeeping,
                                            setDirty,
                                            readOnly = false,
                                          }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(false);
  const [animation] = useAutoAnimate();
  const classes = useStyles();
  
  React.useEffect(() => {
    fetchAdministrationData('Category', machineId, { text: null }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAdministrationData]);
  
  const steps = [
    <FormattedMessage id="page.step" defaultMessage="Step {number}" values={{ number: 1 }} />,
    <FormattedMessage id="page.step" defaultMessage="Step {number}" values={{ number: 2 }} />,
    <FormattedMessage id="page.step" defaultMessage="Step {number}" values={{ number: 3 }} />,
  ];
  
  const isLastStep = activeStep === steps.length - 1;
  const isSecondStep = activeStep === steps.length - 2;
  
  const flattenFields = (fields) => fields.flatMap((field) =>
    field.fields ? [field, ...flattenFields(field.fields)] : [field],
  );
  
  const mapFormDataToDefaultValues = (config, formData) => {
    return Object.fromEntries(
      flattenFields(config)
        .filter(({ name }) => name)
        .map(({ name }) => [name, formData?.[name]]));
  };
  
  const defaultValuesBookkeeping = administrationBookkeeping && mapFormDataToDefaultValues(CONFIG_BOOKKEEPING, administrationBookkeeping);
  const defaultValuesNextStep = administrationBookkeeping && mapFormDataToDefaultValues(CONFIG_BOOKKEEPING_NEXT_STEP, administrationBookkeeping);
  
  const defaultValues = {
    ...defaultValuesBookkeeping,
    ...defaultValuesNextStep,
  };
  
  const handleStepChange = (step) => {
    if (step === 2) {
      setDirty(false);
    }
    setActiveStep(step);
  };
  
  const openIntegrationTab = () => {
    window.open(INTEGRATION_URL, '_blank');
  };
  
  const findUpdatedKeys = (main, compareWith) => Object.keys(main).filter(key => compareWith[key] && main[key] !== compareWith[key]);
  
  const renderFields = (fields) => (
    fields.map((field, fieldIndex) => (
      <div key={fieldIndex}>
        {field.header && <p className={classes.formHeader}>{field.header}</p>}
        {field.label && (<Field
          name={field.name}
          component={FormTextField}
          label={field.label}
          validate={field.validate}
          setDirty={setDirty}
          disabled={readOnly}
        />)}
        {field.fields?.length > 0 && renderFields(field.fields)}
      </div>
    ))
  );
  
  const getDifferenceTable = (classes, differentKeys, administrationBookkeeping, values) => {
    return (
      <div className={classes.bookkeepingTable}>
        <div className={classes.headerRow}>
          <div className={classes.cell}>
            <FormattedMessage id="page.administration.bookkeeping.item" defaultMessage="Item" />
          </div>
          <div className={classes.cell}>
            <FormattedMessage id="page.administration.bookkeeping.oldValue" defaultMessage="Old value" />
          </div>
          <div className={classes.cell}>
            <FormattedMessage id="page.administration.bookkeeping.newValue" defaultMessage="New value" />
          </div>
        </div>
        {differentKeys.length > 0 && differentKeys.map((key) => (
          <div key={key} className={classes.dataRow}>
            <div className={classes.cell}>{key}</div>
            <div className={classes.cell}>{administrationBookkeeping[key]}</div>
            <div className={classes.cell}>{values[key]}</div>
          </div>
        ))}
      </div>
    );
  };
  
  const renderStepBookkeepingDetails = () => {
    return <div ref={animation} className={classes.stepWrapper}>
      <Box display={'flex'} flexDirection={'row'} justifyContent={'space-around'}>
        {CONFIG_BOOKKEEPING.map((section, index) => (
          <div key={index}>
            {section.header && <p className={classes.formHeader}>{section.header}</p>}
            {section.fields && <div>{renderFields(section.fields)}</div>}
          </div>
        ))}
      </Box>
      
      <div className={classes.buttonsWrapper}>
        <CommonButton
          onClick={() => openIntegrationTab()}
          variant="outlined"
          label={<>
            <FormattedMessage
              id="page.administration.bookkeeping.openIntegrations"
              defaultMessage="Connect with bookkeeping-systems"
            />
            &nbsp;
            <MergeTypeSharp />
          </>}
        />
        
        <CommonButton
          disabled={true}
          onClick={() => {
            fetchAdministrationData('Category', machineId, { text: null }, 0);
            setPopupActive(true);
          }}
          variant="outlined"
          label={<>
            <AddOutlined />
            &nbsp;
            <FormattedMessage
              id="page.administration.bookkeeping.editCategories"
              defaultMessage="Edit profit/loss accounts"
            />
          </>}
        />
      </div>
    </div>;
  };
  
  const renderStepNextBookkeepingDetails = () => {
    return <div ref={animation} className={classes.stepWrapper}>
      <Box display={'flex'} flexDirection={'row'} justifyContent={'space-around'}>
        {CONFIG_BOOKKEEPING_NEXT_STEP.map((section, index) => (
          <div key={index}>
            {section.header && <p className={classes.formHeader}>{section.header}</p>}
            {section.fields && <div>{renderFields(section.fields)}</div>}
          </div>))}
      </Box>
    </div>;
  };
  
  const renderStepSaveStatus = (differentKeys, values, isLoading, saveStatus) => {
    return <div ref={animation} className={classes.stepWrapper}>
      {!isLoading && saveStatus !== null && (
        <>
          <p className={classes.successHeader}>
            <FormattedMessage
              id={saveStatus ? 'page.administration.bookkeeping.settingsUpdated' : 'page.administration.bookkeeping.savingError'}
              defaultMessage={saveStatus ? 'Bookkeeping settings updated!' : 'Error on saving bookkeeping data'}
            />
            <Lottie
              loop={false}
              animationData={saveStatus ? successAnimation : errorAnimation}
              style={saveStatus ? successStyles : errorStyles}
            />
          </p>
          
          {saveStatus === true && differentKeys.length > 0 && (
            getDifferenceTable(classes, differentKeys, administrationBookkeeping, values)
          )}
        </>
      )}
    </div>;
  };
  
  const renderStepContent = (activeStep, errors, saveStatus, values, isLoading) => {
    const differentKeys = findUpdatedKeys(values, administrationBookkeeping);
    
    switch (activeStep) {
      case 0:
        return renderStepBookkeepingDetails();
      case 1:
        return renderStepNextBookkeepingDetails();
      case 2:
        return renderStepSaveStatus(differentKeys, values, isLoading, saveStatus);
      default:
        return null;
    }
  };
  
  const renderStepButtons = (errors, dirty) => {
    const hasError = Object.keys(errors).length !== 0;
    
    return <div className={classes.buttonsStepWrapper}>
      <CommonButton
        disabled={activeStep === 0}
        onClick={() => handleStepChange(activeStep - 1)}
        label={<>
          <ArrowBack />&nbsp;<FormattedMessage id="button.prev" defaultMessage="Back" />
        </>}
      />
      
      {!isLastStep && <CommonButton
        type="submit"
        disabled={hasError || (isSecondStep && !dirty)}
        label={<>
          {isSecondStep
            ? <FormattedMessage id="button.save" defaultMessage="Save" />
            : <FormattedMessage id="button.next" defaultMessage="Next" />}
          &nbsp;
          <ArrowForward />
        </>}
      />}
    </div>;
  };
  
  const handleSubmit = (values, { setSubmitting }) => {
    setSubmitting(false);
    handleStepChange(activeStep + 1);
    if (activeStep === 1 && machineId) {
      saveBookkeepingData(machineId, values, setSubmitting, setSaveStatus, setLoading);
    }
  };
  
  return (
    <Paper className={classes.paperWrapper}>
      <Stepper className={classes.stepContainer} activeStep={activeStep} alternativeLabel>
        {steps.map((label, index) => (
          <Step key={index}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
  
      <Formik enableReinitialize initialValues={defaultValues} onSubmit={handleSubmit}>
        {({ errors, values, dirty }) => {
          return <Form className={classes.formWrapper}>
            {renderStepContent(activeStep, errors, saveStatus, values, isLoading)}
            {renderStepButtons(errors, dirty)}
          </Form>;
        }}
      </Formik>

    </Paper>
  );
};

AdministrationBookkeepingContent.propTypes = {
  machineId: PropTypes.string,
  setPopupActive: PropTypes.func,
  fetchAdministrationData: PropTypes.func,
  saveBookkeepingData: PropTypes.func,
  administrationBookkeeping: PropTypes.object,
  setDirty: PropTypes.func,
};

const decorate = compose(
  connect(store => ({
      machines: store.user.machines,
      vatOptions: store.administrationData.vatOptions,
      administrationBookkeeping: store.administrationData.administrationBookkeeping,
    }),
    dispatch => bindActionCreators({ fetchVATOptions, fetchAdministrationData, saveBookkeepingData }, dispatch),
  ),
);

export default decorate(AdministrationBookkeepingContent);